import { useParams } from 'react-router-dom';
import { useEmailConfigurationGet } from '../hooks/use-emailConfiguration.hook';
import { memo, useMemo } from 'react';
import EmailConfigurationDetailsComponent from '../components/emailConfiguration-details.component';
import { DetailItem } from '../../../../common/RenderDetails';
import { EmailConfiguration } from '../types/emailConfiguration.types';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { formatDate } from '../../../../../utils/date.util';
import { WithSysAdminPermissionCheck } from '@components/auth/WithSysAdminPermissionCheck';
import PageTitle from '@components/common/PageTitle';
import { maskPassword } from '../../../../../utils/crypto.util';

const EmailConfigurationDetailsContainer = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { data, isLoading, error } = useEmailConfigurationGet(id || '');

  const prepareDetailsData = useMemo(
    () =>
      (dataObj: EmailConfiguration | undefined): DetailItem[] => {
        if (!dataObj) return [];

        const detailItems: DetailItem[] = [];
        // prepare data to display in the details card
        Object.entries(dataObj).forEach(([key, value]) => {
          if (key === 'createdAt' || key === 'updatedAt') {
            detailItems.push({
              label: t(`common.${key}.label`),
              value: formatDate(value),
            });
            return;
          }

          if (key === 'id') {
            detailItems.push({
              label: t(`common.${key}.label`),
              value: value.toString(),
            });
            return;
          }
          if (key === 'password') {
            detailItems.push({
              label: t(`emailConfiguration.${key}.label`),
              value: maskPassword(String(value ?? '')),
            });
            return;
          }
          detailItems.push({
            label: t(`emailConfiguration.${key}.label`),
            value: String(value),
          });
        });

        return detailItems;
      },
    [t]
  );

  if (!id) {
    return (
      <ErrorFallback
        details={t('crud.details.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );
  }

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.details.title', { entity: t('emailConfiguration.title') })} />
        <HeaderRow
          title={t('crud.details.title', {
            entity: t('emailConfiguration.title'),
          })}
          showBackButton={true}
          trailTarget={id}
        />
        <ErrorFallback
          details={t('crud.details.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('crud.details.title', { entity: t('emailConfiguration.title') })} />
      <HeaderRow
        title={t('crud.details.title', { entity: t('emailConfiguration.title') })}
        showBackButton={true}
        trailTarget={id}
        isSysAdmin={true}
      />
      <div className="row mt-3">
        <EmailConfigurationDetailsComponent
          data={prepareDetailsData(data?.data)}
          success={data?.success ?? false}
          isLoading={isLoading}
          error={error}
        />
      </div>
    </>
  );
};

export default WithSysAdminPermissionCheck(
  memo(EmailConfigurationDetailsContainer),
  'emailConfiguration.view'
);
